import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { splitEvery } from 'ramda';
import { Slider } from 'js/components/common/Slider';
import { uniqueId } from 'js/utils/methods.util';

import * as hooks from '../../../../hooks';
import * as helpers from '../../../../helpers';
import { CarouselItem } from '../CarouselItem';
import { CAROUSEL_SLIDER_OPTIONS, CAROUSEL_MIN_ITEMS_PER_SLIDE } from '../../../../constants';

import style from './GridPagingSlider.scss';

export const GridPagingSlider = () => {
    const frame = useRef();
    const containerRef = useRef();
    const winnerItemRef = useRef();
    const topWinners = hooks.useTopWinners();
    const [gamesPerSlide, setGamesPerSlide] = useState(CAROUSEL_MIN_ITEMS_PER_SLIDE);

    const slides = useMemo(() => {
        if (!topWinners.length) {
            return [];
        }

        const result = splitEvery(gamesPerSlide, topWinners);
        const lastSlide = result[result.length - 1];

        if (lastSlide.length < gamesPerSlide) {
            const emptyItems = new Array(gamesPerSlide - lastSlide.length);

            emptyItems.fill(null);
            result[result.length - 1] = [...lastSlide, ...emptyItems];
        }

        return result;
    }, [gamesPerSlide, topWinners]);

    const updateSlides = useCallback(() => {
        if (containerRef.current && winnerItemRef.current) {
            setGamesPerSlide(helpers.getTotalGamesCount(containerRef.current, winnerItemRef.current));
        }
    }, []);

    const setSlideRef = (ref) => {
        containerRef.current = ref;
        updateSlides();
    };

    const setWinnerItemRef = (ref) => {
        winnerItemRef.current = ref;
        updateSlides();
    };

    const throttledCalculatedGamePerSlide = useCallback(() => {
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(updateSlides);
    }, [updateSlides]);

    useEffect(() => {
        window.addEventListener('resize', throttledCalculatedGamePerSlide);

        return () => {
            window.removeEventListener('resize', throttledCalculatedGamePerSlide);
        };
    }, [throttledCalculatedGamePerSlide]);

    return (
        <Slider
            swipe={false}
            renderAll={false}
            showDisabledArrows={true}
            showControlsAtStart={true}
            refs={{ slide: setSlideRef }}
            options={CAROUSEL_SLIDER_OPTIONS}
            classNames={{
                ...style,
                slide: style.slide,
                leftArrow: style.leftArrow,
                rightArrow: style.rightArrow,
                sliderContainer: style.sliderContainer
            }}
            className={`${style.sliderWrapper} ${slides.length <= 1 ? style.noControls : ''} cms-top-winners-carousel-slider`}
        >
            {slides.map((slide, i) => (
                <Fragment key={`${slide.length}_${i}_${slides[0][0]}`}>
                    {slide.map((topWinner, index) => topWinner
                        ? (
                            <CarouselItem
                                {...topWinner}
                                key={topWinner.id}
                                className={style.topWinner}
                                {...(i === 0 && index === 0 && { setWinnerItemRef })}
                            />
                        )
                        : <div key={uniqueId(index)} className={style.topWinner} />
                    )}
                </Fragment>
            ))}
        </Slider>
    );
};
